'use client';

import { Fragment, ReactNode } from 'react';
import { flexRender } from '@tanstack/react-table';
import { cj, cm } from 'src/utils/classNames';
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableHeadRow,
	TableRow,
} from 'src/components/primitives/Table/Table';
import Paginator from 'src/components/Paginator';
import Collapsible from 'src/components/Collapsible/Collapsible';
import { DataTableProps } from 'src/components/primitives/Table/types';
import {
	PINNED_COLUMN_CLASSES,
	TABLE_DEFAULT_EMPTY_MESSAGE,
} from 'src/components/primitives/Table/consts';
import { useDataTableSetup } from 'src/components/primitives/Table/useDataTableSetup';

interface Props<T> extends DataTableProps<T> {
	isRowExpanded: (row: T) => boolean;
	renderExpandedRow: (row: T) => ReactNode;
	onRowClick: (row: T) => void;
}

export function ExpandableRowDataTable<T>({
	columns,
	data,
	isRowExpanded,
	onRowClick,
	renderExpandedRow,
	emptyMessage = TABLE_DEFAULT_EMPTY_MESSAGE,
	className,
	containerClassName,
	emptyClassName,
	meta,
	columnPinning,
	footer,
	pagination,
	dataTestId,
}: Props<T>) {
	const {
		table,
		containerRef,
		getColumnStyle,
		getStuckClass,
		showPaginator,
		isEmpty,
	} = useDataTableSetup({ data, columns, meta, columnPinning, pagination });

	return (
		<div className="flex h-full flex-col">
			<div className="min-h-0 flex-1 overflow-auto">
				<Table
					className={cm(isEmpty && ['h-full', emptyClassName], className)}
					containerClassName={cm(
						!showPaginator && 'rounded-b-sm',
						containerClassName
					)}
					containerRef={containerRef}
					dataTestId={dataTestId}
				>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableHeadRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const isPinned = header.column.getIsPinned();
									const headerCellClassName =
										header.column.columnDef.meta?.headerCellClassName;
									return (
										<TableHead
											key={header.id}
											style={getColumnStyle(header.column)}
											className={cm(
												isPinned && PINNED_COLUMN_CLASSES[isPinned],
												getStuckClass(isPinned),
												headerCellClassName
											)}
										>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
										</TableHead>
									);
								})}
							</TableHeadRow>
						))}
					</TableHeader>

					<TableBody className={cj(isEmpty && 'h-full')}>
						{isEmpty ? (
							<tr className="h-full bg-container-bg">
								<TableCell
									colSpan={columns.length}
									className="h-full text-center typo-label-sm text-text-secondary"
								>
									{emptyMessage}
								</TableCell>
							</tr>
						) : (
							table.getRowModel().rows.map((row) => {
								const isExpanded = isRowExpanded(row.original);

								return (
									<Fragment key={row.id}>
										<TableRow
											onClick={() => onRowClick(row.original)}
											className={cm(
												'cursor-pointer',
												isExpanded && 'bg-main-bg hover:bg-main-bg'
											)}
											data-testid={dataTestId ? `${dataTestId}-row` : undefined}
										>
											{row.getVisibleCells().map((cell) => {
												const isPinned = cell.column.getIsPinned();
												const bodyCellClassName =
													cell.column.columnDef.meta?.bodyCellClassName;
												return (
													<TableCell
														key={cell.id}
														style={getColumnStyle(cell.column)}
														className={cm(
															isPinned && PINNED_COLUMN_CLASSES[isPinned],
															getStuckClass(isPinned),
															bodyCellClassName,
															isExpanded && 'bg-main-bg group-hover:bg-main-bg'
														)}
														data-testid={
															cell.column.columnDef.meta?.bodyCellTestId
														}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</TableCell>
												);
											})}
										</TableRow>
										<Collapsible.Root open={isExpanded} asChild>
											<Collapsible.Content asChild>
												<TableRow>
													<TableCell
														colSpan={columns.length}
														className="border-0 bg-main-bg p-0 hover:bg-main-bg"
													>
														{renderExpandedRow(row.original)}
													</TableCell>
												</TableRow>
											</Collapsible.Content>
										</Collapsible.Root>
									</Fragment>
								);
							})
						)}
					</TableBody>

					{footer && (
						<TableFooter>
							<TableRow className="hover:bg-container-bg">{footer}</TableRow>
						</TableFooter>
					)}
				</Table>
			</div>

			{showPaginator && pagination && (
				<Paginator
					pageIndex={pagination.pageIndex}
					pageSize={pagination.pageSize}
					totalCount={pagination.totalCount}
					onPageChange={pagination.onPageChange}
					isTokenBasedPagination
					className="rounded-b-sm"
				/>
			)}
		</div>
	);
}
